import { NavLink, Outlet } from "react-router-dom";
import { useRef, useState } from "react";
import { apiFetch } from "../lib/api";
import { formatDateTime } from "../lib/format";
import {
  clearPendingIdempotencyKey,
  getOrCreatePendingIdempotencyKey,
  type PendingIdempotencyEntry,
} from "../lib/idempotency";
import { Panel } from "../components/layout/Panel";
import { StatusBadge } from "../components/layout/StatusBadge";
import { WarningPanel } from "../components/layout/WarningPanel";
import { useInvalidation } from "./invalidation";
import { useSession } from "./session";
import { useSystemStatusSummary } from "./useSystemStatusSummary";

const WORKSPACES = [
  { path: "/planning", label: "Plan" },
  { path: "/procurement", label: "Buy" },
  { path: "/operations", label: "Receive" },
  { path: "/finance", label: "Cash" },
  { path: "/feedback", label: "Review" },
  { path: "/system", label: "Settings" },
];

export function AppShell() {
  const { logout, loading: sessionLoading, error: sessionError, refreshSession, refreshingSession } =
    useSession();
  const { markDirty } = useInvalidation();
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const pendingMutationKeysRef = useRef<Record<string, PendingIdempotencyEntry>>({});
  const { errors, normalizedSystemStatus, staleStreams, systemStatus } = useSystemStatusSummary();
  const shellErrors = actionError ? [actionError, ...errors] : errors;

  async function handleManualSync() {
    setLoading(true);
    setActionError(null);
    const requestBody = "";
    const key = getOrCreatePendingIdempotencyKey(
      pendingMutationKeysRef.current,
      "manual-sync",
      requestBody,
    );
    try {
      await apiFetch("/integrations/sync", {
        method: "POST",
        headers: { "Idempotency-Key": key },
      });
      clearPendingIdempotencyKey(pendingMutationKeysRef.current, "manual-sync");
      markDirty("all");
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRecomputeFeedback() {
    setLoading(true);
    setActionError(null);
    try {
      await apiFetch("/feedback/recompute", { method: "POST" });
      markDirty(["planning", "procurement", "finance", "feedback"]);
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    setActionError(null);
    try {
      await logout();
    } catch (err) {
      setActionError((err as Error).message);
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="topbar-brand">
          <div className="eyebrow">Amazon Inventory Intelligence</div>
          <h1>Stay in stock without the spreadsheet chaos</h1>
          <p>Simple planning, buying, receiving, and cash visibility in one guided workspace.</p>
          <nav className="workspace-nav" aria-label="Workspace navigation">
            {WORKSPACES.map((workspace) => (
              <NavLink
                key={workspace.path}
                to={workspace.path}
                className={({ isActive }) => `workspace-link${isActive ? " active" : ""}`}
              >
                {workspace.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="topbar-actions">
          <div className="actions">
            <button onClick={handleManualSync} disabled={loading}>
              Refresh Data
            </button>
            <button onClick={handleRecomputeFeedback} disabled={loading}>
              Refresh Insights
            </button>
            <button className="button-secondary" onClick={handleLogout} disabled={loading || sessionLoading}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <Panel className="status-strip glass-panel">
        <div className="status-chip">
          <strong>System</strong>
          <StatusBadge tone={normalizedSystemStatus || "neutral"}>{normalizedSystemStatus || "-"}</StatusBadge>
        </div>
        <div className="status-chip">
          <strong>Background Sync</strong>
          <span>{systemStatus?.worker_heartbeat_seconds ?? "-"}</span>
        </div>
        <div className="status-chip">
          <strong>Attention Needed</strong>
          <span>{staleStreams.length}</span>
        </div>
        {staleStreams.slice(0, 2).map((stream) => (
          <div key={stream.stream} className="status-chip wide-chip">
            <strong>{stream.stream}</strong>
            <span>{formatDateTime(stream.last_success_at)}</span>
          </div>
        ))}
      </Panel>

      {sessionError && (
        <Panel className="warning-panel" aria-live="polite">
          <h2>Session issue</h2>
          <p>{sessionError}</p>
          <button type="button" onClick={() => refreshSession()} disabled={refreshingSession}>
            {refreshingSession ? "Retrying session..." : "Retry Session"}
          </button>
        </Panel>
      )}

      <WarningPanel title="Shell Warnings" warnings={shellErrors} />

      <div className="workspace-stack">
        <Outlet />
      </div>
    </main>
  );
}
